import {remCalc} from '_themeUtils'

const navigationStylesCommons = {
    height: 'auto',
    width: 'auto',
    pos: 'unset',
    p: 'spacing.2',
    _after: {
        display: 'none',
    },
}

const productTileOverlay = {
    ProductTileOverlay: {
        baseStyle: () => ({
            wrapper: {
                pos: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
                maxH: {base: remCalc(430), md: 'auto'},
                // Figma mobile drawer: uniform 10px padding. Desktop keeps its existing padding.
                pt: {base: remCalc(10), md: 'spacing.4'},
                px: {base: remCalc(10), md: 'spacing.4'},
                pb: {base: remCalc(10), md: 'spacing.8'},
                // Figma drawer background is #ffffffe5 (white @ ~90%) on both breakpoints. Mobile
                // previously used opaque white (sizeSelector.default); align it to the same
                // translucent value used on desktop (sizeSelector.desktop = alpha.light 90%).
                bg: 'color.background.sizeSelector.desktop',
                gap: 'spacing.none',
                zIndex: 'dropdown',
            },
            selectionTitle: {
                textStyle: 'paragraphMd',
                fontWeight: 400,
                // Figma section titles ("Colori/Taglie disponibili"): paragraph-md 16/20, tracking 0.8px.
                letterSpacing: remCalc(0.8),
                mb: 'spacing.4',
            },
            selectionTitleError: {
                color: 'color.text.error',
            },
            addToCartButtonWrapper: {
                pos: 'relative',
                w: 'full',
            },
            addToCartNoSizeWrapper: {
                pos: 'absolute',
                w: 'full',
                left: 0,
                right: 0,
                textAlign: 'center',
            },
            addToCartNoSize: {
                textStyle: 'paragraphSm',
                fontWeight: 400,
                color: 'color.text.error',
            },
            swiperWrapper: {
                w: '100%',
                maxW: '100%',
                pt: remCalc(15),
                sx: {
                    '.swiper': {
                        margin: 0,
                    },
                    '.swiper-slide': {
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    },
                },
            },
            // Figma: title + navigation arrows on one header row (arrows top-right, aligned to the title).
            colorsHeaderRow: {
                w: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
            },
            // The color title sits in a header row, so neutralize the shared bottom margin;
            // the wrapper gap provides the separation from the swatch row.
            colorsTitle: {
                mb: 0,
            },
            colorsNavigation: {
                // Figma "Chevron": two 16px icons, 15px apart, right-aligned.
                gap: remCalc(15),
                alignItems: 'center',
                flexShrink: 0,
            },
            colorsNavigationButton: {
                ...navigationStylesCommons,
                p: 0,
                minW: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Default arrow colour = #262626; when Swiper marks the arrow disabled (first slide
                // for prev, last slide for next) it turns grey (#989898) per Figma.
                // The chevron colour lives on the inner <path fill="..."> (presentation attribute,
                // lowest specificity), so we override that fill directly in the disabled state.
                color: 'color.icon.black',
                transition: 'color 0.15s ease',
                '&.swiper-button-disabled': {
                    cursor: 'default',
                    pointerEvents: 'none',
                    'svg path': {
                        fill: 'color.icon.grey.500',
                    },
                },
            },
            // Fixed 1px divider between the scrollable list and the footer togglers (always visible).
            mobileListDivider: {
                w: '100%',
                h: remCalc(1),
                bg: 'color.border.grey.400',
                flexShrink: 0,
            },
            // Figma footer "Selettori": row gap 10px, 10px horizontal padding, centered.
            // Min height keeps the footer consistent whether or not the colour slot (with its 42px
            // separator) is present, so the single "Taglia" slot doesn't look squeezed.
            mobileTogglesRow: {
                w: '100%',
                minH: remCalc(42),
                alignItems: 'center',
                gap: remCalc(10),
                px: remCalc(10),
                py: remCalc(8),
            },
            // Each toggler slot fills its half: chevron + label (+ colour dot on the colour slot).
            toggleSlot: {
                flex: '1 1 0',
                minW: 0,
                alignItems: 'center',
                gap: remCalc(10),
                cursor: 'pointer',
                bg: 'color.background.transparent',
            },
            toggleSlotLabel: {
                textStyle: 'paragraphSm',
                fontWeight: 400,
                letterSpacing: remCalc(0.7),
                color: 'color.text.black',
                flex: '1 1 0',
                textAlign: 'left',
            },
            // Selected size value shown at the right of the Taglia slot (mirrors the colour dot).
            toggleSlotValue: {
                textStyle: 'paragraphSm',
                fontWeight: 400,
                letterSpacing: remCalc(0.7),
                color: 'color.text.black',
                flexShrink: 0,
            },
            // Figma: 42px vertical divider between the two togglers.
            toggleSeparator: {
                w: remCalc(1),
                h: remCalc(42),
                bg: 'color.border.grey.400',
                flexShrink: 0,
            },
            // Current-colour indicator at the right of the Colore toggler (Figma outer box 24px).
            // Rendered as "selected" so it shows the grey outer ring + inner dark dot.
            colorTogglerSwatch: {
                position: 'relative',
                w: remCalc(24),
                h: remCalc(24),
                flexShrink: 0,
            },
            colorsRowWrapper: {
                w: '100%',
                alignItems: 'flex-start',
                gap: 0,
                // Desktop: swatch row uses 15px bottom padding + divider. Mobile: the vertical list
                // rows carry their own dividers, so no extra bottom padding on base.
                pb: {base: 0, md: remCalc(15)},
                borderBottom: {md: '1px solid'},
                borderColor: {md: 'color.border.grey.400'},
            },
            // Figma: swatch row scrolls horizontally, 5px gap between items, 15px top padding under the header.
            colorsRow: {
                alignItems: 'flex-start',
                columnGap: remCalc(5),
                flexWrap: 'wrap',
                pt: remCalc(15),
                w: '100%',
            },
            // Figma "Swatch colore PLP": fixed 75px column, 5px gap between dot and label, centered.
            colorWrapper: {
                gap: remCalc(5),
                alignItems: 'center',
                justifyContent: 'flex-start',
                minW: remCalc(75),
                maxW: remCalc(75),
            },
            // Figma "Color dots" outer box (24px). The two-ring visual (inner dark dot + grey outer
            // ring on selection) is drawn by the ColorCircle atom via its `selected` prop.
            colorSwatch: {
                position: 'relative',
                w: remCalc(24),
                h: remCalc(24),
                flexShrink: 0,
                cursor: 'pointer',
            },
            // ─── Mobile colour list (Figma "Color slot") ──────────────────────────────
            // Vertical scrollable list: ~3 rows visible then scroll (row ≈ 50px with 16px padding → cap 150px).
            colorsListMobile: {
                w: '100%',
                gap: 0,
                alignItems: 'stretch',
                maxH: remCalc(150),
                overflowY: 'auto',
            },
            // Each colour row: dot + label, 10px horizontal / 16px vertical padding, bottom divider.
            // Figma colour slot uses a 15px gap between the dot and the label.
            colorSlot: {
                w: '100%',
                gap: remCalc(15),
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: remCalc(10),
                py: remCalc(16),
                borderBottom: '1px solid',
                borderColor: 'color.border.grey.400',
                cursor: 'pointer',
                bg: 'color.background.transparent',
            },
            // Figma "Color dots" outer box (mobile 26px). Two-ring visual handled by ColorCircle (`selected`).
            colorSlotSwatch: {
                position: 'relative',
                w: remCalc(26),
                h: remCalc(26),
                flexShrink: 0,
            },
            colorSlotLabel: {
                textStyle: 'paragraphSm',
                letterSpacing: remCalc(0.7),
                fontWeight: 400,
                color: 'color.text.black',
                textAlign: 'left',
            },
            // Figma label: paragraph-xs 12px/14px, tracking 0.6px, default text color, centered, wraps.
            colorName: {
                textStyle: 'paragraphXs',
                letterSpacing: remCalc(0.6),
                fontWeight: 400,
                textAlign: 'center',
                color: 'color.text.black',
                w: '100%',
                noOfLines: 2,
            },
            addToCartRow: {
                w: '100%',
                // Mobile: add clear breathing room above the ACQUISTA button so the footer togglers
                // aren't squeezed against it (especially when there's no colour block). Desktop keeps
                // its top divider + padding.
                pt: {base: remCalc(12), md: 'spacing.4'},
                gap: 'spacing.6',
                borderTop: {base: 'none', md: '1px solid'},
                borderColor: {md: 'color.border.grey.300'},
            },
            rangeRealPrice: {
                textStyle: 'paragraphSm',
                letterSpacing: remCalc(0.8),
                whiteSpace: 'nowrap',
            },
        }),
        variants: {
            wishlist: {
                wrapper: {
                    h: 'full',
                    maxH: 'full',
                    pt: {base: 'spacing.7', md: 'spacing.7'},
                    pb: {base: 'spacing.10', md: 'spacing.10'},
                    px: {base: 'spacing.6', md: 'spacing.14'},
                    overflowY: 'auto',
                    bg: 'color.background.sizeSelector.desktop',
                },
                productNotAvailableWrapper: {
                    w: 'full',
                    h: 'full',
                    justify: 'center',
                    align: 'center',
                    spacing: 'spacing.3',
                    textAlign: 'center',
                },
                notAvailableLabel: {
                    textStyle: 'paragraphMd',
                    fontWeight: 400,
                },
                shareContainer: {
                    spacing: 'spacing.4',
                    align: 'flex-start',
                },
                shareLabel: {
                    textStyle: 'paragraphXs',
                    fontWeight: 400,
                    letterSpacing: remCalc(3),
                    textTransform: 'uppercase',
                },
                shareButton: {
                    cursor: 'pointer',
                    spacing: 'spacing.2',
                },
                skuLabel: {
                    textStyle: 'paragraphXs',
                    color: 'color.text.grey.600',
                },
                overlayMainContent: {
                    w: 'full',
                    flexGrow: 1,
                    align: 'flex-start',
                    justify: 'center',
                    spacing: 0,
                },
                selectedColorLabel: {
                    textStyle: 'paragraphMd',
                    fontWeight: 400,
                    letterSpacing: remCalc(0.6),
                    mb: {base: 'spacing.1', xl: 'spacing.4'},
                },
                selectedColor: {
                    textTransform: 'uppercase',
                },
                selectedSizeLabel: {
                    textStyle: 'paragraphMd',
                    fontWeight: 400,
                    letterSpacing: remCalc(0.6),
                    mb: 'spacing.12',
                },
                addProductToCartWrapper: {
                    position: 'relative',
                    w: 'full',
                },
                goToProductButton: {
                    mt: 'spacing.5',
                    mx: 'auto',
                },
            },
        },
    },
}

export default productTileOverlay
